import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplications, updateApplication, updateApplicationData } from "../../../store/reducers/applicationSlice";
import { createNotification } from "../../../store/reducers/notificationSlice";

import { showSuccess, showError } from '../../../helpers/toast';
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { Link } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const AppInReview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
    setSearchTerm("");
  };

  // debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      let term = searchTerm;
      if (searchBy === "applicationId" && term.startsWith("GB-")) {
        term = term.replace("GB-", "");
      }
      setDebouncedSearchTerm(term);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, searchBy]);

  // Fetch applications when date filter changes - filter by state in API call for better performance
  useEffect(() => {
    let queryParams = {
      state: 'application_submitted_by_company_user_to_company' // Filter at API level for faster loading
    };

    // SECURITY: Add company_id filter at API level for company_admin
    if (user.user?.user_role === "company_admin" && user.user?.company_id) {
      queryParams.company_id = user.user.company_id;
    }
    // SECURITY: Add user_id filter at API level for employees
    else if (user.user?.user_role === "employee" && user.user?.id && !debouncedSearchTerm) {
      queryParams.user_id = user.user.id;
    }

    if (startDate) {
      queryParams.createdFrom = moment(startDate).format("YYYY-MM-DD");
    }
    if (endDate) {
      queryParams.createdTo = moment(endDate).format("YYYY-MM-DD");
    }

    // Add search parameters if debouncedSearchTerm is present
    if (debouncedSearchTerm) {
      queryParams[searchBy] = debouncedSearchTerm;
    }

    dispatch(fetchApplications({
      accessToken: authToken,
      queryParams,
      fetchAllPages: true // Fetch all pages to show all applications
    }));
  }, [authToken, dispatch, startDate, endDate, user.user?.user_role, user.user?.company_id, user.user?.id, debouncedSearchTerm, searchBy]);

  // Optimized merge and filter - using useMemo for better performance
  const mergedAndFilteredData = useMemo(() => {
    if (!applications.data?.length) {
      return [];
    }

    // Create a Map for O(1) user lookup instead of O(n) find()
    const userMap = new Map(
      (getallUserdata || []).map((userItem) => [userItem.id, userItem])
    );
    const userCompanyId = user.user.company_id;

    // Merge data efficiently
    const mergedData = applications.data.map((appItem) => {
      const userItem = userMap.get(appItem.user_id);
      if (userItem) {
        return {
          ...appItem,
          applicationId: appItem.id,
          ...userItem,
          userId: userItem.id,
          created_at: appItem.created_at,
          updated_at: appItem.updated_at,
        };
      }
      return { ...appItem, applicationId: appItem.id };
    });

    // SECURITY: Filter by role, company, and state
    const userRole = user.user?.user_role;
    const currentUserId = user.user?.id;

    const filteredData = mergedData.filter((item) => {
      // Handle both camelCase and snake_case company ID fields
      const itemCompanyId = item.company_id || item.companyId;
      const itemUserId = item.user_id || item.userId || item.user?.id;

      let shouldShow = false;

      if (userRole === "company_admin") {
        // Company admin can see all applications in their company
        shouldShow = String(itemCompanyId) === String(userCompanyId);
      } else if (userRole === "employee") {
        // Employee can only see their own applications within their company
        const isOwnApplication = String(itemUserId) === String(currentUserId);
        const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
        shouldShow = isOwnApplication && hasCorrectCompany;
      } else {
        // Default: filter by company (fallback)
        shouldShow = String(itemCompanyId) === String(userCompanyId);
      }

      if (!shouldShow) return false;

      if (!searchQuery) return true;

      // Optimized search - only check string values
      // If using server search, we can relax this local filter if debouncedSearchTerm is true
      if (debouncedSearchTerm && (searchTerm || searchQuery)) return true;
      if (!searchQuery && !searchTerm) return true;

      const term = searchQuery || searchTerm;
      const searchLower = term.toLowerCase();
      return Object.values(item).some(
        (value) =>
          value &&
          typeof value === 'string' &&
          value.toLowerCase().includes(searchLower)
      );
    });

    return filteredData;
  }, [applications.data, getallUserdata, user.user?.company_id, user.user?.user_role, user.user?.id, searchQuery, searchTerm, debouncedSearchTerm]);

  // Update filteredApplication when merged data changes
  useEffect(() => {
    setFilteredApplication(mergedAndFilteredData);
  }, [mergedAndFilteredData]);

  const handleSearchInputChange = (e) => {
    let val = e.target.value;
    if (searchBy === "applicationId") {
      const digits = val.replace(/\D/g, "");
      if (digits.length > 0) {
        val = `GB-${digits}`;
      } else {
        val = "";
      }
    }
    setSearchQuery(val);
    setSearchTerm(val);
  };

  const getTotalPages = () => {
    return Math.ceil(filteredApplication?.length / 10);
  };

  const handleDownload = useCallback(async (item) => {
    const { applicationId, userId } = item;

    setDownloadingIds((prev) => [...prev, applicationId]);

    try {
      // Call the download function directly since it's not a Redux action
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/zip",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `application_${applicationId}_files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess('Files downloaded successfully!');
    } catch (error) {
      showError(`Download failed: ${error.message}`);
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
    }
  }, [authToken]);

  const handleViewAndEdit = (item) => {
    if (!item.applicationId) {
      showError('Error: Application ID not found');
      return;
    }

    try {
      // Navigate to edit page
      navigate(`/application-edit/${item.applicationId}`);
    } catch (error) {
      showError('Error navigating to edit page');
    }
  };

  const handleAssignToDiscrepancies = useCallback(async (item) => {
    try {
      await dispatch(updateApplicationData({
        id: item.applicationId,
        state: "application_sent_to_discrepancy_by_company",
        userId: user.user.id,  // ← ADDED: Required user ID
        token: authToken,
        json_data: item.json_data,

      }));

      // dispatch(createNotification({
      //   user_id: item.user_id || item.userId,
      //   applicationId: item.applicationId,
      //   applicationName: item.name,
      //   status: "application_sent_to_discrepancy_by_company",
      //   accessToken: authToken,
      //   sendToCompanyAdmins: true,
      //   companyName: user.user?.company_name,
      //   employeeName: item.userName || item.name
      // }));

      showSuccess('Application assigned in discrepancies!');
      navigate(`/view-discrepancies/company/${item.applicationId}`);
      // Refresh the applications list
      dispatch(fetchApplications({
        accessToken: authToken,
        fetchAllPages: true // Fetch all pages to show all applications
      }));
    } catch (error) {
      showError('Failed to assign application in discrepancies');
    }
  }, [dispatch, authToken, user.user.id, navigate]);

  const handleSubmitToGB = useCallback(async (item) => {
    try {
      await dispatch(updateApplicationData({
        id: item.applicationId,
        state: "in_process",
        userId: user.user.id,
        token: authToken,
        json_data: item.json_data,
      }));

      // Notify Employee
      dispatch(createNotification({
        user_id: item.user_id || item.userId,
        applicationId: item.applicationId,
        applicationName: item.name,
        status: "in_process",
        accessToken: authToken,
        sendToGamingBoardAdmins: true,
        companyName: user.user?.company_name,
        employeeName: item.userName || item.name
      }));

      showSuccess('Application submitted to Gaming Board successfully!');
      // Refresh the applications list with the same filters
      let queryParams = {
        state: 'application_submitted_by_company_user_to_company' // Filter at API level for faster loading
      };

      // SECURITY: Add company_id filter at API level for company_admin
      if (user.user?.user_role === "company_admin" && user.user?.company_id) {
        queryParams.company_id = user.user.company_id;
      }
      // SECURITY: Add user_id filter at API level for employees
      else if (user.user?.user_role === "employee" && user.user?.id) {
        queryParams.user_id = user.user.id;
      }

      if (startDate) {
        queryParams.createdFrom = moment(startDate).format("YYYY-MM-DD");
      }
      if (endDate) {
        queryParams.createdTo = moment(endDate).format("YYYY-MM-DD");
      }

      dispatch(fetchApplications({
        accessToken: authToken,
        queryParams,
        fetchAllPages: true // Fetch all pages to show all applications
      }));
    } catch (error) {
      showError('Failed to submit application to Gaming Board');
    }
  }, [dispatch, authToken, user.user.id, user.user?.user_role, user.user?.company_id, startDate, endDate]);

  const handleSubmitToCompany = async (item) => {
    // Only allow for admin role
    if (user.user?.user_role !== "admin") {
      showError('Only admin users can submit to company');
      return;
    }

    try {
      await dispatch(updateApplication({
        id: item.applicationId,
        state: "application_submitted_to_company",
        token: authToken
      }));
      showSuccess('Application submitted to company successfully!');
      // Refresh the applications list with the same filters
      let queryParams = {
        state: 'application_submitted_by_company_user_to_company' // Filter at API level for faster loading
      };

      // SECURITY: Add company_id filter at API level for company_admin
      if (user.user?.user_role === "company_admin" && user.user?.company_id) {
        queryParams.company_id = user.user.company_id;
      }
      // SECURITY: Add user_id filter at API level for employees
      else if (user.user?.user_role === "employee" && user.user?.id) {
        queryParams.user_id = user.user.id;
      }

      if (startDate) {
        queryParams.createdFrom = moment(startDate).format("YYYY-MM-DD");
      }
      if (endDate) {
        queryParams.createdTo = moment(endDate).format("YYYY-MM-DD");
      }

      dispatch(fetchApplications({
        accessToken: authToken,
        queryParams,
        fetchAllPages: true // Fetch all pages to show all applications
      }));
    } catch (error) {
      showError('Failed to submit application to company');
    }
  };

  // Pagination + sorting data
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId)
      .slice((currentPage - 1) * 10, currentPage * 10);
  }, [filteredApplication, currentPage]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * 10 + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong title={row.name}>
                {row.name?.length > 50 ? `${row.name.substring(0, 50)}...` : row.name}
              </strong>
              <span>{row.categoryName}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedBy",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;
          return (

            <div className="boardTitle">
              <strong>{row.userName || "N/A"}</strong>
              <span>{row.userEmail || "N/A"}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => (
          <span className="dateText">
            {user.user?.user_role === "employee" ? "Under Review" : "Submitted to Company"}
          </span>
        ),
      }),
      columnHelper.accessor("created_at", {
        header: "Submitted Date",
        cell: (info) => (
          <span className="dateText">{info.getValue()?.slice(0, 10)}</span>
        ),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    <Link
                      onClick={() => handleDownload(row)}
                      className="dropdown-item"
                      style={{
                        opacity: downloadingIds.includes(row.applicationId) ? 0.6 : 1,
                        pointerEvents: downloadingIds.includes(row.applicationId) ? 'none' : 'auto'
                      }}
                    >
                      {downloadingIds.includes(row.applicationId) ? 'Downloading...' : 'Download'}
                    </Link>
                  </li>
                  {user.user?.user_role === "company_admin" && (
                    <>
                      <li>
                        <Link
                          to={`/application-edit/${row.applicationId}`}
                          className="dropdown-item"
                        >
                          View and Edit
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => handleAssignToDiscrepancies(row)}
                          className="dropdown-item"
                        >
                          Add to Discrepancies
                        </Link>
                      </li>
                      <li>
                        <Link
                          onClick={() => handleSubmitToGB(row)}
                          className="dropdown-item"
                        >
                          Submit to GB
                        </Link>
                      </li>

                      {/* <li>
                        <Link
                          onClick={() => handleSubmitToCompany(row)}
                          className="dropdown-item"
                        >
                          Submit to Company
                        </Link>
                      </li> */}
                    </>
                  )}

                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [downloadingIds, user.user?.user_role, handleDownload, handleAssignToDiscrepancies, handleSubmitToGB, currentPage]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Add loading state (same as in-process / approved / rejected)
  if (applications.fetchLoading || applications.loading) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="spinner"></div>
              <p className="mt-3">Loading applications...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Employee Applications - In Review</h2>
            </div>
            <div className="boardHeadRight">
              <select onChange={handleSearchByChange} value={searchBy} >
                <option value="applicationId">Search by Application ID</option>
                <option value="username">Search by Employee Name</option>

              </select>
              <form className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={handleSearchInputChange}
                  value={searchTerm}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="boardContent table-responsive">
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}

                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={table.getHeaderGroups()?.[0]?.headers.length} style={{ textAlign: "center", padding: "20px" }}>
                      <p>No in-review applications found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default AppInReview;
